/**
 * „Materiál má inú hrúbku" — otázka pri výbere v importe (KOLO 217).
 *
 * User si vybral okno s tabuľkou: dotknuté dielce (zostava, dielec, ks,
 * 1 → 0,8 mm) a dve voľby — dielce dostanú hrúbku materiálu, alebo späť
 * k výberu. Voľba ide s priradením (aj so Zapamätať), ďalší import sa už
 * nepýta; riadok v tabuľke ostane s vetou „1 → 0,8 mm".
 */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { MapovaniePolozka } from "@/api/hooks/useTopsolidRaw";
import { dielceRiadku, zmenaHrubky } from "@/lib/import/hrubkaMaterialu";
import { formatMm } from "@/lib/splitRules";

export function InaHrubkaDialog({
  polozka,
  zRiadku,
  zMaterialu,
  material,
  menaZostav,
  onPotvrd,
  onSpat,
}: {
  polozka: MapovaniePolozka;
  zRiadku: number;
  zMaterialu: number;
  /** Kód alebo meno vybraného materiálu. */
  material: string | undefined;
  menaZostav?: Record<string, string> | null;
  onPotvrd: () => void;
  onSpat: () => void;
}): JSX.Element {
  const dielce = dielceRiadku(polozka, menaZostav);
  const zmena = zmenaHrubky(zRiadku, zMaterialu);
  return (
    <Dialog open onOpenChange={(o) => !o && onSpat()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Materiál má inú hrúbku</DialogTitle>
          <DialogDescription>
            V TopSolide majú tieto dielce {formatMm(zRiadku)} mm,{" "}
            {material ? (
              <span className="font-mono">{material}</span>
            ) : (
              "vybraný materiál"
            )}{" "}
            má {formatMm(zMaterialu)} mm. Po importe budú mať hrúbku materiálu —
            v TopSolide ostane {formatMm(zRiadku)} mm, kým model neopravíš.
          </DialogDescription>
        </DialogHeader>
        {dielce.length > 0 ? (
          <div className="max-h-64 overflow-y-auto rounded border">
            <Table aria-label="Dielce inej hrúbky">
              <TableHeader>
                <TableRow>
                  <TableHead className="px-2 py-1">Zostava</TableHead>
                  <TableHead className="px-2 py-1">Dielec</TableHead>
                  <TableHead className="w-12 px-2 py-1 text-right">
                    Ks
                  </TableHead>
                  <TableHead className="w-28 px-2 py-1 text-right">
                    Hrúbka
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dielce.map((d) => (
                  <TableRow key={`${d.zostava}|${d.dielec}`}>
                    <TableCell className="px-2 py-1 text-xs">
                      {d.zostava}
                    </TableCell>
                    <TableCell className="px-2 py-1 font-mono text-xs">
                      {d.dielec}
                    </TableCell>
                    <TableCell className="px-2 py-1 text-right text-xs">
                      {d.ks}
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-2 py-1 text-right text-xs">
                      {zmena}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onSpat}>
            Vybrať iný materiál
          </Button>
          <Button onClick={onPotvrd}>
            Dielce dostanú {formatMm(zMaterialu)} mm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
